import {Injectable} from '@angular/core';
import {Table} from 'dexie';
import * as Sentry from "@sentry/angular";
import {dexie} from "../../../dexie";
import {PictureKind, StoredPicture, UploadState} from "../../core/models/stored-picture";

@Injectable({
  providedIn: 'root'
})
export class PictureStoreService {

  private readonly inMemoryPictures = new Map<string, StoredPicture>();
  private persistent = true;

  private table(): Table<StoredPicture, string> {
    if (!dexie.pictures) {
      throw new Error('IndexedDB is not available');
    }
    return dexie.pictures;
  }

  async put(picture: StoredPicture): Promise<void> {
    this.inMemoryPictures.set(picture.key, picture);
    await this.persist(table => table.put(picture, picture.key));
  }

  async putAll(pictures: StoredPicture[]): Promise<void> {
    pictures.forEach(picture => this.inMemoryPictures.set(picture.key, picture));
    await this.persist(table => table.bulkPut(pictures, pictures.map(picture => picture.key)));
  }

  async get(key: string): Promise<StoredPicture | undefined> {
    if (!this.persistent) {
      return this.inMemoryPictures.get(key);
    }
    try {
      return await this.table().get(key);
    } catch (error) {
      this.degrade(error);
      return this.inMemoryPictures.get(key);
    }
  }

  async all(): Promise<StoredPicture[]> {
    if (!this.persistent) {
      return [...this.inMemoryPictures.values()];
    }
    try {
      const pictures = await this.table().toArray();
      pictures.forEach(picture => this.inMemoryPictures.set(picture.key, picture));
      return pictures;
    } catch (error) {
      this.degrade(error);
      return [...this.inMemoryPictures.values()];
    }
  }

  async byKind(kind: PictureKind): Promise<StoredPicture[]> {
    return (await this.all()).filter(picture => picture.kind === kind);
  }

  async byState(...states: UploadState[]): Promise<StoredPicture[]> {
    return (await this.all())
      .filter(picture => states.includes(picture.uploadState))
      .sort((a, b) => a.capturedAt - b.capturedAt);
  }

  async nextPending(): Promise<StoredPicture | undefined> {
    return (await this.byState(UploadState.PENDING))[0];
  }

  async patch(key: string, changes: Partial<StoredPicture>): Promise<void> {
    const known = this.inMemoryPictures.get(key);
    if (known) {
      this.inMemoryPictures.set(key, {...known, ...changes});
    }
    await this.persist(table => table.update(key, changes));
  }

  async markUploading(key: string): Promise<void> {
    const picture = await this.get(key);
    await this.patch(key, {
      uploadState: UploadState.UPLOADING,
      attempts: (picture?.attempts ?? 0) + 1,
      lastError: undefined
    });
  }

  async markUploaded(key: string, uploadedAt = Date.now()): Promise<void> {
    await this.patch(key, {uploadState: UploadState.UPLOADED, uploadedAt, lastError: undefined});
  }

  async markPending(key: string): Promise<void> {
    await this.patch(key, {uploadState: UploadState.PENDING, uploadedAt: undefined});
  }

  async markFailed(key: string, lastError: string): Promise<void> {
    await this.patch(key, {uploadState: UploadState.FAILED, lastError});
  }

  async remove(key: string): Promise<void> {
    this.inMemoryPictures.delete(key);
    await this.persist(table => table.delete(key));
  }

  async removeAll(keys: string[]): Promise<void> {
    keys.forEach(key => this.inMemoryPictures.delete(key));
    await this.persist(table => table.bulkDelete(keys));
  }

  async clear(): Promise<void> {
    this.inMemoryPictures.clear();
    await this.persist(table => table.clear());
  }

  async retagAnalysis(analysisId: number): Promise<void> {
    const pictures = await this.all();
    await this.putAll(pictures.map(picture => ({
      ...picture,
      analysisId,
      uploadState: UploadState.PENDING,
      uploadedAt: undefined,
      attempts: 0,
      lastError: undefined
    })));
  }

  private async persist(operation: (table: Table<StoredPicture, string>) => Promise<unknown>): Promise<void> {
    if (!this.persistent) {
      return;
    }
    try {
      await operation(this.table());
    } catch (error) {
      this.degrade(error);
    }
  }

  private degrade(error: unknown): void {
    if (!this.persistent) {
      return;
    }
    this.persistent = false;
    Sentry.captureMessage('IndexedDB unusable, pictures kept in memory for this session', {
      level: 'warning',
      extra: {error}
    });
  }
}
